import React, { useMemo, useState } from 'react'
import { connect, shallowEqual, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import TelecomDto from '../dto/TelecomDto'
import GenericReferencialDto from '../../../dto/GenericReferencialDto'
import ContributorDto from '../../../../referencial/components/contributor/dto/ContributorDto'
import Select from '../../../../components/forms/Select'
import Input from '../../../../components/forms/Input'
import Textarea from '../../../../components/forms/Textarea'
import { getMatTypes } from '../../../../utils/MaterielUtils'
import NetworkDto from '../../../../referencial/components/network/dto/NetworkDto'
import Checkbox from '../../../../components/forms/Checkbox'
import NumberField from '../../../../components/forms/NumberField'
import PowerSupplyTypeDto from '../../powerSupply/dto/PowerSupplyTypeDto'
import moment from 'moment'
import { authorizeContributors, authorizeFilter } from 'utils/HabilitationUtil'
import User from 'account/dto/User'
import { removeNullKeys } from 'utils/StoreUtils'
import { omit } from 'lodash'
import ConfirmModal from 'components/modal/ConfirmModal'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import { TELECOM_ID } from 'materiel/constants/MaterielConstants'
import { isFieldRequired } from 'materiel/utils/MaterielUtils'
import { DATEACHAT_OBLIGATOIRE, NUMIMEI_OBLIGATOIRE } from 'materiel/constants/MaterielSettingRuleConstant'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { Grid, Stack } from '@mui/material'

const TelecomPanel = ({
    telecom = {},
    telecomTypes = [],
    active = false,
    disabled = true,
    contributors = [],
    accountUser = {},
    networks = [],
    powerSupplyTypes = [],
    onChange = () => { },
}) => {
    const {
        materielSettingRules,
    } = useSelector(store => ({
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }), shallowEqual)

    const [isOpen, setOpen] = useState(false)
    const [selectedType, setSelectedType] = useState()

    const rules = useMemo(() => {
        return materielSettingRules.filter(({ idMaterielType }) => idMaterielType === TELECOM_ID)
    }, [materielSettingRules])

    return (
        <Stack columnGap={2}>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={4}>
                    <Select
                        options={getMatTypes(telecomTypes, telecom.telecomType)}
                        label={i18n.telecomType}
                        onChange={v => {
                            const type = telecomTypes.find(({ id }) => v === id)
                            if (type) {
                                setOpen(true)
                                setSelectedType(type)
                            } else {
                                onChange({ telecomType: v })
                            }
                        }}
                        obligatory
                        value={telecom.telecomType}
                        nullLabel='&nbsp;'
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={4}>
                    <Input
                        title={i18n.immoNumber}
                        value={telecom.reference}
                        onChange={value => onChange({ reference: value })}
                        active={active}
                        disabled={telecom.isEquipment || disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={6}>
                    <Input
                        title={i18n.imeiAndSerialNumber}
                        value={telecom.imei}
                        onChange={value => onChange({ imei: value })}
                        active={active}
                        disabled={disabled}
                        obligatory={isFieldRequired(rules, NUMIMEI_OBLIGATOIRE)}
                    />
                </Grid>
                <Grid size={6}>
                    <Checkbox
                        label={i18n.digitalLineOrRTC}
                        checked={telecom.digitalLine}
                        onChange={value => onChange({ digitalLine: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={6}>
                    <MultiContributorsAutocomplete
                        options={authorizeContributors(contributors, accountUser, telecom.manufacturerId)}
                        label={i18n.manufacturer}
                        onChange={manufacturerId => onChange({ manufacturerId })}
                        values={telecom.manufacturerId}
                        disabled={telecom.isEquipment || disabled}
                        keyLabel='labelDisplay'
                    />
                </Grid>
                <Grid size={6}>
                    <MultiContributorsAutocomplete
                        options={authorizeContributors(contributors, accountUser, telecom.providerId)}
                        label={i18n.provider}
                        onChange={providerId => onChange({ providerId })}
                        values={telecom.providerId}
                        disabled={telecom.isEquipment || disabled}
                        keyLabel='labelDisplay'
                    />
                </Grid>
            </Grid>
            <Select
                options={authorizeFilter(networks, accountUser, telecom.networkCode)}
                label={i18n.network}
                onChange={value => onChange({ networkCode: value })}
                value={telecom.networkCode}
                nullLabel='&nbsp;'
                active={active}
                disabled={telecom.isEquipment || disabled}
            />
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={3}>
                    <Select
                        options={getMatTypes(powerSupplyTypes, telecom.powerSupplyType)}
                        label={i18n.powerSupplyType}
                        onChange={value => onChange({ powerSupplyType: value })}
                        value={telecom.powerSupplyType}
                        nullLabel='&nbsp;'
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={i18n.batteryCount}
                        value={telecom.pileNumber}
                        onChange={value => onChange({ pileNumber: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={i18n.supplyTime}
                        value={telecom.supplyTime}
                        onChange={value => onChange({ supplyTime: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <Checkbox
                        label={i18n.desiccant}
                        checked={telecom.desiccant === '1'}
                        onChange={value => onChange({ desiccant: value ? '1' : '0' })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={3}>
                    <SimpleDatePicker
                        id='purchaseDate'
                        label={i18n.purchaseDate}
                        value={telecom.purchaseDate}
                        onChange={v => onChange({ purchaseDate: v })}
                        disabled={telecom.isEquipment || disabled}
                        max={telecom.openDate}
                        obligatory={isFieldRequired(rules, DATEACHAT_OBLIGATOIRE)}
                    />
                </Grid>
                <Grid size={3}>
                    <SimpleDatePicker
                        label={i18n.warrantyExpirationDate}
                        value={telecom.warrantyTime && moment(telecom.purchaseDate).add(telecom.warrantyTime, 'months').valueOf()}
                        disabled
                    />
                </Grid>
                {/* <DatePicker
                onChange={(_, v) => onChange({ withdrawalDate: v })}
                id='withdrawalDate'
                title={i18n.withdrawalDate}
                value={telecom.withdrawalDate}
                active={active}
                disabled={telecom.isEquipment || disabled}
            />
            <DatePicker
                onChange={(_, v) => onChange({ replacementDate: v })}
                id='replacementDate'
                title={i18n.replaceDate}
                value={telecom.replacementDate}
                active={active}
                disabled={telecom.isEquipment || disabled}
            /> */}
            </Grid>
            <Textarea
                title={i18n.comment}
                value={telecom.comment}
                onChange={value => onChange({ comment: value })}
                active={active}
                disabled={disabled}
            />
            <ConfirmModal
                isOpen={isOpen}
                title={i18n.materielDefaultValuesMessage}
                onValidate={() => {
                    setOpen(false)
                    onChange(removeNullKeys({
                        telecomType: selectedType.id,
                        ...omit(selectedType, ['id', 'code', 'label', 'name', 'comment', 'updateDate', 'updateLogin', 'startDate', 'endDate', 'icon']),
                    }))
                }}
                onReject={() => {
                    setOpen(false)
                    onChange({ telecomType: selectedType.id })
                }}
                onClose={() => setOpen(false)}
            />
        </Stack >
    )
}

TelecomPanel.propTypes = {
    id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    disabled: PropTypes.bool,
    active: PropTypes.bool,
    onChange: PropTypes.func,
    telecom: PropTypes.instanceOf(TelecomDto),
    telecomTypes: PropTypes.arrayOf(GenericReferencialDto),
    contributors: PropTypes.arrayOf(PropTypes.instanceOf(ContributorDto)),
    powerSupplyTypes: PropTypes.arrayOf(PropTypes.instanceOf(PowerSupplyTypeDto)),
    networks: PropTypes.arrayOf(PropTypes.instanceOf(NetworkDto)),
    accountUser: PropTypes.instanceOf(User),
}

const mapStateToProps = store => ({
    contributors: store.ContributorReducer.contributors,
    telecomTypes: store.TelecomReducer.telecomTypes,
    powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
    networks: store.NetworkReducer.networks,
    accountUser: store.AccountReducer.accountUser,
})

export default connect(mapStateToProps)(TelecomPanel)
